import React, { useCallback, useEffect, useState } from 'react'
import apiClient from '../../services/api'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/LoadingSpinner'
import { uploadImageToCloudinary } from '../../services/cloudinaryUpload'

const galleryFilters = [
  { key: 'all', label: 'All Images' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' }
]

const initialForm = { imageUrl: '', imagePublicId: '', title: '', description: '', active: true }

const AdminGallery = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(initialForm)
  const [editId, setEditId] = useState(null)
  const [imageSource, setImageSource] = useState('url')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const { addToast } = useToast()

  const loadGallery = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/cms/gallery')
      setItems(response.data)
    } catch (err) {
      addToast('Error loading gallery', 'danger')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    loadGallery()
  }, [loadGallery])

  const resetForm = () => {
    setForm(initialForm)
    setEditId(null)
    setImageSource('url')
  }

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      addToast('Please choose a valid image file.', 'warning')
      return
    }

    setUploadingImage(true)

    uploadImageToCloudinary(file, 'medical-clinic/gallery')
      .then((upload) => {
        setForm((prev) => ({ ...prev, ...upload }))
        setImageSource('upload')
        addToast('Image uploaded successfully.', 'success')
      })
      .catch(() => {
        addToast('Unable to upload the selected image.', 'danger')
      })
      .finally(() => {
        setUploadingImage(false)
      })
  }

  const save = async (e) => {
    e.preventDefault()
    try {
      if (editId) {
        await apiClient.put(`/api/cms/gallery/${editId}`, form)
        addToast('Gallery updated', 'success')
      } else {
        await apiClient.post('/api/cms/gallery', form)
        addToast('Gallery item added', 'success')
      }
      resetForm()
      loadGallery()
    } catch (err) {
      addToast('Error saving gallery item', 'danger')
    }
  }

  const remove = async (id) => {
    if (!window.confirm('Delete item?')) return
    try {
      await apiClient.delete(`/api/cms/gallery/${id}`)
      addToast('Deleted', 'success')
      loadGallery()
    } catch (err) {
      addToast('Delete failed', 'danger')
    }
  }

  if (loading) return <LoadingSpinner />

  const filteredItems = items.filter((item) => {
    if (statusFilter === 'active') return item.active
    if (statusFilter === 'inactive') return !item.active
    return true
  })

  const filterCount = (key) => {
    if (key === 'active') return items.filter((item) => item.active).length
    if (key === 'inactive') return items.filter((item) => !item.active).length
    return items.length
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h2>Gallery</h2>
          <p>
            Add image URLs or upload directly to Cloudinary, then use status filters to control what appears on the public homepage gallery.
          </p>
        </div>
      </div>

      <div className="card p-3 mb-4 shadow-sm admin-form-card">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="mb-0">{editId ? 'Edit Gallery Item' : 'Add Gallery Item'}</h5>
          {editId && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={save} className="row g-3">
          <div className="col-12">
            <div className="btn-group" role="group" aria-label="Image source type">
              <button
                type="button"
                className={`btn ${imageSource === 'url' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setImageSource('url')}
              >
                Use Image URL
              </button>
              <button
                type="button"
                className={`btn ${imageSource === 'upload' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setImageSource('upload')}
              >
                Upload Image
              </button>
            </div>
          </div>
          <div className="col-md-4">
            {imageSource === 'url' ? (
              <input
                value={form.imageUrl}
                onChange={(event) => setForm((prev) => ({ ...prev, imageUrl: event.target.value }))}
                className="form-control"
                placeholder="https://example.com/clinic.jpg"
                required
              />
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="form-control"
                required={!form.imageUrl}
              />
            )}
          </div>
          <div className="col-md-3">
            <input
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              className="form-control"
              placeholder="Title"
              required
            />
          </div>
          <div className="col-md-3">
            <input
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              className="form-control"
              placeholder="Short description"
            />
          </div>
          <div className="col-md-2 d-flex align-items-center">
            <div className="form-check">
              <input
                id="galleryActive"
                type="checkbox"
                className="form-check-input"
                checked={form.active}
                onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))}
              />
              <label className="form-check-label" htmlFor="galleryActive">Active</label>
            </div>
          </div>
          <div className="col-12">
            <button type="submit" className="btn btn-primary" disabled={uploadingImage}>
              {editId ? 'Update Item' : 'Add Item'}
            </button>
          </div>
          {(form.imageUrl || uploadingImage) && (
            <div className="col-12">
              <div className="card p-3">
                <h5 className="mb-3">Image Preview</h5>
                {uploadingImage ? (
                  <p className="mb-0 text-muted">Preparing selected image...</p>
                ) : (
                  <img
                    src={form.imageUrl}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '260px', objectFit: 'cover', borderRadius: '0.5rem' }}
                  />
                )}
              </div>
            </div>
          )}
        </form>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filter-chips">
          {galleryFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`admin-filter-chip${statusFilter === filter.key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="admin-filter-chip-count">{filterCount(filter.key)}</span>
            </button>
          ))}
        </div>
        <div className="admin-note">{filteredItems.length} image{filteredItems.length === 1 ? '' : 's'} shown</div>
      </div>

      <div className="row g-3">
        {filteredItems.map((item) => (
          <div key={item._id} className="col-md-4 mb-3">
            <div className="card h-100">
              <img src={item.imageUrl} alt={item.title} className="card-img-top" style={{height: '150px', objectFit:'cover'}} />
              <div className="card-body">
                <h5 className="card-title">{item.title}</h5>
                <p className="card-text">{item.description}</p>
                <span className={`admin-status-badge ${item.active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                  {item.active ? 'Active' : 'Inactive'}
                </span>
                <button
                  className="btn btn-sm btn-secondary me-2"
                  onClick={() => {
                    setForm({
                      imageUrl: item.imageUrl,
                      imagePublicId: item.imagePublicId || '',
                      title: item.title || '',
                      description: item.description || '',
                      active: item.active
                    })
                    setEditId(item._id)
                    setImageSource(item.imagePublicId ? 'upload' : 'url')
                  }}
                >
                  Edit
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(item._id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AdminGallery
